/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import type { UserRegistration } from '@sportninja/common/hooks/useUserRegistrationData'
import type { Country } from '@sportninja/common/types/Country'
import { formatRegistrationMoney } from '../../utils/formatRegistrationMoney'
import type { RefundSummary } from '../../utils/registrationRows'

interface RefundTooltipProps {
  refund: RefundSummary
  country?: Country
}

/** What was refunded, and why. */
export const RefundTooltip = ({ refund, country }: RefundTooltipProps) => (
  <div>
    {refund.transactionId ? (
      <div css={transactionStyle}>
        <strong>Transaction ID:</strong>
        <span>{refund.transactionId}</span>
      </div>
    ) : null}
    <div>
      <strong>Refunded:</strong>{' '}
      {formatRegistrationMoney(refund.total, country)}
    </div>
    {refund.count > 1 ? (
      <div>
        <strong>Transactions:</strong> {refund.count}
      </div>
    ) : null}
    {refund.reason ? (
      <div>
        <strong>Reason:</strong> {refund.reason}
      </div>
    ) : null}
    {refund.note ? (
      <div>
        <strong>Note:</strong> {refund.note}
      </div>
    ) : null}
  </div>
)

interface CouponTooltipProps {
  coupon: NonNullable<UserRegistration['coupon']>
  country?: Country
}

/** The coupon applied to a registration. */
export const CouponTooltip = ({ coupon, country }: CouponTooltipProps) => {
  const isPercentage = coupon.discount_type === 'percentage'

  return (
    <div>
      <div>
        <strong>Coupon Code:</strong> {coupon.code}
      </div>
      <div>
        <strong>Discount:</strong>{' '}
        {isPercentage
          ? `${coupon.discount}%`
          : formatRegistrationMoney(
              coupon.discount_amount || coupon.discount,
              country
            )}
      </div>
      <div>
        <strong>Type:</strong> {isPercentage ? 'Percentage' : 'Amount'}
      </div>
    </div>
  )
}

// ─── Styles ──────────────────────────────────────────────────────

const transactionStyle = css`
  display: flex;
  flex-direction: column;
  margin-bottom: 4px;
`
